import type { ICustomerUtilityBillLineItem } from '../customer-utility-bill-file/interface';

export enum BillProcessingStatus {
    Pending = 'PENDING',
    DownloadSuccess = 'DOWNLOAD_SUCCESS',
    DownloadFailed = 'DOWNLOAD_FAILED',
    ReparseQueued = 'REPARSE_QUEUED',
    ParsingInProgress = 'PARSING_IN_PROGRESS',
    ParsingSuccess = 'PARSING_SUCCESS',
    ParsingFailed = 'PARSING_FAILED',
    ParsingNeedsReview = 'PARSING_NEEDS_REVIEW',
}

// Human-readable labels for each processing status (status tags, filter options, saved-filter chips).
export const PROCESSING_STATUS_LABEL: Record<BillProcessingStatus, string> = {
    [BillProcessingStatus.Pending]: 'Pending',
    [BillProcessingStatus.DownloadSuccess]: 'Download Success',
    [BillProcessingStatus.DownloadFailed]: 'Download Failed',
    [BillProcessingStatus.ReparseQueued]: 'Re-parse Queued',
    [BillProcessingStatus.ParsingInProgress]: 'Parsing In Progress',
    [BillProcessingStatus.ParsingSuccess]: 'Parsing Success',
    [BillProcessingStatus.ParsingFailed]: 'Parsing Failed',
    [BillProcessingStatus.ParsingNeedsReview]: 'Needs Review',
};

// Mirrors the lean CustomerUtilityBillDto the API returns (UI-consumed fields only).
export interface ICustomerUtilityBill {
    id: string;
    fileId: string | null;

    customerName: string;
    siteName: string;
    siteNumber: string | null;
    address: string;
    city: string;
    state: string;
    market: string | null;
    accountNumber: string | null;
    enrollmentId: string | null;
    meterNumber: string;
    controlProviderName: string | null;

    vendor: string | null;
    zone: string | null;
    utility: string | null;

    zipCode: string | null;
    rateClass: string;
    billingKw: number | null;
    billingKwh: number | null;
    billingPeriodStart: string | null;
    billingPeriodEnd: string | null;

    verifiedAt: string | null;
    verifiedBy: string | null;
    notes: string | null;

    processingStatus: BillProcessingStatus;
    processingError: string | null;

    parsedAt: string | null;
    updatedAt: string;
}

// Mirrors the backend rules (CustomerUtilityBillEntity.EDITABLE_STATUSES / VERIFIABLE_STATUSES).
// A bill can be edited only once the parser has produced a result (incl. FAILED, so a human can salvage it);
// it can be (un)verified only when it carries reviewable data (SUCCESS / NEEDS_REVIEW).
export const EDITABLE_BILL_STATUSES: BillProcessingStatus[] = [
    BillProcessingStatus.ParsingSuccess,
    BillProcessingStatus.ParsingNeedsReview,
    BillProcessingStatus.ParsingFailed,
];

export const VERIFIABLE_BILL_STATUSES: BillProcessingStatus[] = [BillProcessingStatus.ParsingSuccess, BillProcessingStatus.ParsingNeedsReview];

export const isBillEditable = (bill: ICustomerUtilityBill): boolean => EDITABLE_BILL_STATUSES.includes(bill.processingStatus);

export const isBillVerifiable = (bill: ICustomerUtilityBill): boolean => VERIFIABLE_BILL_STATUSES.includes(bill.processingStatus);

// EDITABLE_BILL_STATUSES is the single gate: a status in it can be edited; any other status is view-only
// (the modal still opens read-only). So there's no separate "viewable" list — editability is the only check.

// Offered once the PDF is available to re-read: a finished parse (success/needs-review/failed), a
// downloaded-but-not-yet-parsed bill (DOWNLOAD_SUCCESS), or one already queued (re-submitting just re-enqueues
// it). DOWNLOAD_FAILED has no PDF to re-parse; PARSING_IN_PROGRESS is still parsing.
// (Mirrors the backend CustomerUtilityBillEntity.REPARSE_REQUESTABLE_STATUSES.)
export const REPARSABLE_BILL_STATUSES: BillProcessingStatus[] = [
    ...EDITABLE_BILL_STATUSES,
    BillProcessingStatus.DownloadSuccess,
    BillProcessingStatus.ReparseQueued,
];

export const isBillReparsable = (bill: ICustomerUtilityBill): boolean => REPARSABLE_BILL_STATUSES.includes(bill.processingStatus);

// Non-terminal states while a (re-)parse is in flight; anything else counts as done, including a bill that
// was never queued for reparse at all. Used to know when to stop polling a bill for its reparse outcome.
const REPARSE_IN_PROGRESS_STATUSES: BillProcessingStatus[] = [BillProcessingStatus.ReparseQueued, BillProcessingStatus.ParsingInProgress];

export const isReparseInProgress = (bill: ICustomerUtilityBill): boolean => REPARSE_IN_PROGRESS_STATUSES.includes(bill.processingStatus);

// Whether the bill carries any extracted data. Mirrors the backend's deriveParsingStatus: with no extracted
// fields a record is FAILED and therefore cannot be verified. Zero is a real value; '' / null / undefined are not.
const hasValue = (value: string | number | null): boolean => value !== null && value !== undefined && (value as unknown) !== '';

export const billHasExtractedData = (bill: ICustomerUtilityBill): boolean =>
    hasValue(bill.zipCode) ||
    hasValue(bill.billingKw) ||
    hasValue(bill.billingKwh) ||
    hasValue(bill.billingPeriodStart) ||
    hasValue(bill.billingPeriodEnd);

// Client-side validation of the editable fields. Mirrors the backend rules (CustomerUtilityBillEntity
// VALIDATION_RULES) so the operator gets inline feedback and we don't round-trip an invalid save.
export interface BillFieldErrors {
    meterNumber?: string;
    rateClass?: string;
    zipCode?: string;
    billingKw?: string;
    billingKwh?: string;
    billingPeriod?: string;
}

const ZIP_CODE_PATTERN = /^\d{5}(-?\d{4})?$/;

// Mirror the backend caps (CustomerUtilityBillRequestDto): kW is 0–MAX; kWh must be positive and ≤ MAX.
export const MAX_BILLING_VALUE = 1_000_000;

export const validateBillFields = (bill: ICustomerUtilityBill): BillFieldErrors => {
    const errors: BillFieldErrors = {};

    // Meter + rate class identify the bill (and match it to its source-file row) — the backend keeps them non-null.
    if (!bill.meterNumber?.trim()) {
        errors.meterNumber = 'Meter number is required';
    }
    if (!bill.rateClass?.trim()) {
        errors.rateClass = 'Rate class is required';
    }
    if (bill.zipCode && !ZIP_CODE_PATTERN.test(bill.zipCode.trim())) {
        errors.zipCode = 'Enter a 5-digit (or 9-digit ZIP+4) ZIP code';
    }
    if (bill.billingKw != null && (bill.billingKw < 0 || bill.billingKw > MAX_BILLING_VALUE)) {
        errors.billingKw = `Must be between 0 and ${MAX_BILLING_VALUE.toLocaleString()}`;
    }
    if (bill.billingKwh != null && (bill.billingKwh <= 0 || bill.billingKwh > MAX_BILLING_VALUE)) {
        errors.billingKwh = `Must be greater than 0 and at most ${MAX_BILLING_VALUE.toLocaleString()}`;
    }
    if (bill.billingPeriodStart && bill.billingPeriodEnd && new Date(bill.billingPeriodStart) > new Date(bill.billingPeriodEnd)) {
        errors.billingPeriod = 'Start date must be on or before the end date';
    }

    return errors;
};

export const hasBillFieldErrors = (errors: BillFieldErrors): boolean => Object.keys(errors).length > 0;

// Bill fields the operator may update (PUT /customer-utility-bills/:id). customerName and vendor are not updatable.
export type TUpdatableBillField =
    | 'siteName'
    | 'siteNumber'
    | 'accountNumber'
    | 'enrollmentId'
    | 'meterNumber'
    | 'rateClass'
    | 'controlProviderName'
    | 'zone'
    | 'utility'
    | 'market'
    | 'address'
    | 'city'
    | 'state'
    | 'zipCode'
    | 'billingKw'
    | 'billingKwh'
    | 'billingPeriodStart'
    | 'billingPeriodEnd';

export const UPDATABLE_BILL_FIELDS: TUpdatableBillField[] = [
    'siteName',
    'siteNumber',
    'accountNumber',
    'enrollmentId',
    'meterNumber',
    'rateClass',
    'controlProviderName',
    'zone',
    'utility',
    'market',
    'address',
    'city',
    'state',
    'zipCode',
    'billingKw',
    'billingKwh',
    'billingPeriodStart',
    'billingPeriodEnd',
];

// Not required to create or update a bill, but the backend only marks it Parsing Success when all of these are filled;
// any empty one leaves it in Needs Review. Shown as (non-blocking) warnings in the edit modal.
export const PARSING_SUCCESS_BILL_FIELDS: TUpdatableBillField[] = [
    'siteName',
    'siteNumber',
    'utility',
    'market',
    'zone',
    'zipCode',
    'billingKw',
    'billingKwh',
    'billingPeriodStart',
    'billingPeriodEnd',
];

export const getMissingParsingSuccessFields = (bill: ICustomerUtilityBill): Set<TUpdatableBillField> =>
    new Set(
        PARSING_SUCCESS_BILL_FIELDS.filter(field => {
            const value = bill[field] as string | number | null;
            return !hasValue(value) || (typeof value === 'string' && !value.trim());
        })
    );

// Backend placeholder for "no rate class".
export const NO_RATE_CLASS = 'NONE';

// Rate class for display: the placeholder (or an empty value) reads as no value.
export const displayRateClass = (rateClass: string | null | undefined): string | null =>
    rateClass && rateClass !== NO_RATE_CLASS ? rateClass : null;

// Rate class "NONE" is the backend placeholder for "no rate class", so it matches an empty/null value in the file.
const normalizeMatchKey = (value: string | null | undefined): string => {
    const normalized = (value ?? '').trim().toLowerCase();
    return normalized === 'none' ? '' : normalized;
};

// The source-file row a bill was created from: same meter number and rate class.
export const findBillMetaRow = (
    bill: Pick<ICustomerUtilityBill, 'meterNumber' | 'rateClass'>,
    meta: ICustomerUtilityBillLineItem[]
): ICustomerUtilityBillLineItem | null => {
    const row =
        meta.find(
            row =>
                normalizeMatchKey(row.meterNumber) === normalizeMatchKey(bill.meterNumber) &&
                normalizeMatchKey(row.rateClass) === normalizeMatchKey(bill.rateClass)
        ) ?? null;

    return row;
};

// Which fields the operator may edit: with no matching source row, every updatable field; with a match, only the
// fields the file left empty (a value in the file is the source of truth and is shown read-only).
export const getEditableBillFields = (metaRow: ICustomerUtilityBillLineItem | null): Set<TUpdatableBillField> =>
    new Set(UPDATABLE_BILL_FIELDS.filter(field => !metaRow || metaRow[field] === null || metaRow[field] === undefined || metaRow[field] === ''));
